<template>
    <section class="relative isolate overflow-hidden bg-white px-6 lg:px-8">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div
                class="flex flex-col justify-center items-start gap-x-16 gap-y-5 xl:gap-28 lg:flex-row lg:justify-between max-lg:max-w-2xl mx-auto max-w-full">
                <div class="w-full lg:w-1/2">
                    <div class="lg:max-w-xl">
                        <div class="mb-6 lg:mb-16">
                            <h6 class="text-lg text-center font-medium text-emerald-600 mb-2 lg:text-left">
                                faqs
                            </h6>
                            <h2
                                class="text-4xl text-center font-bold text-gray-900 leading-[3.25rem] mb-5 lg:text-left">
                                Looking for answers?
                            </h2>
                        </div>
                        <div class="accordion-group text-left" data-accordion="default-accordion">
                            <div v-for="faq in faqs" :key="faq.id"
                                :class="`accordion pb-8 border-b border-solid border-gray-200 text-left ${faq.active}`"
                                :id="`basic-heading-${faq.id}`">
                                <button
                                    class="accordion-toggle group inline-flex items-center justify-between text-xl font-normal leading-8 w-full transition duration-500 hover:text-emerald-600"
                                    :class="faq.active ? 'text-emerald-600 font-medium' : ''"
                                    :aria-controls="`basic-collapse-${faq.id}`" :aria-expanded="faq.active"
                                    @click="toggleAccordion(faq)">
                                    <h5 class="text-left">{{ faq.question }}</h5>
                                    <Transition @before-enter="(el)=>{
                                            el.rotate = 0;
                                            el.color = 'text-gray-900';
                                        }" @enter="(el)=>{
                                            el.rotate = 180;
                                            el.color = 'text-emerald-600';
                                        }" @before-leave="(el)=>{
                                            el.rotate = 180;
                                            el.color = 'text-emerald-600';
                                        }" @leave="(el)=>{
                                            el.rotate = 0;
                                            el.color = 'text-gray-900';
                                        }">
                                        <svg class=""
                                            :class="faq.active ? 'rotate-180 text-emerald-600' : 'rotate-0 text-gray-900'"
                                            width="22" height="22" viewBox="0 0 22 22" fill="none"
                                            xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M16.5 8.25L12.4142 12.3358C11.7475 13.0025 11.4142 13.3358 11 13.3358C10.5858 13.3358 10.2525 13.0025 9.58579 12.3358L5.5 8.25"
                                                stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
                                                stroke-linejoin="round"></path>
                                        </svg>
                                    </Transition>
                                </button>
                                <transition @before-enter="beforeEnter" @enter="enter" @before-leave="beforeLeave"
                                    @leave="leave">
                                    <div :id="`basic-collapse-${faq.id}`" v-show="faq.active"
                                        class="accordion-content w-full px-0 overflow-hidden pr-4"
                                        :aria-labelledby="`basic-heading-${faq.id}`">
                                        <p class="text-base font-normal text-gray-600 ">
                                            {{ faq.answer }}
                                        </p>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup type="module">

import { ref } from 'vue';
import { defineProps } from 'vue';
import { getFaqs } from '@/services/faqService';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { FileUpload } from 'primevue';

const props = defineProps({
    image: {
        type: String,
        default: 'images/home/faqs.jpg',
    },
});

const faqs = ref([]);
const email = ref('');
const resume = ref(null);
const errors = ref({ email: '', resume: '' });

const validateEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value);
};

const allowedFileTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const maxFileSize = 2 * 1024 * 1024; // 2MB

const fetchFaqs = async () => {
    try {
        const response = await getFaqs();
        faqs.value = response.data;
    } catch (error) {
        console.error('Error fetching FAQs:', error);
    }
};

fetchFaqs();

const toggleAccordion = (faq) => {
    faqs.value.forEach((item) => {
        item.active = item.id === faq.id ? !item.active : false;
    });
};

// Transition hooks for height animation
const beforeEnter = (el) => {
    el.style.height = '0';
};

const enter = (el) => {
    el.style.transition = 'height 0.3s ease';
    el.style.height = el.scrollHeight + 'px';
};

const beforeLeave = (el) => {
    el.style.height = el.scrollHeight + 'px';
};

const leave = (el) => {
    el.style.transition = 'height 0.3s ease';
    el.offsetHeight; // force reflow
    el.style.height = '0';
};

</script>

<style scoped>
.p-fileupload.p-fileupload-advanced.p-component {
    width: 100%;
}
</style>